
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./use-toast";
import { createDefaultWorkouts, checkAdminStatus } from "@/utils/auth-utils";

export interface UserData {
  firstName: string;
  lastName: string;
}

export const useAuthActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Signing in user:', email);
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error.message);
        toast({
          title: "Sign in failed",
          description: error.message,
          variant: "destructive",
        });
        setIsLoading(false);
        throw error;
      }
      
      console.log('Sign in successful - auth state change listener will update state');
    } catch (error) {
      console.error("Error signing in:", error);
      setIsLoading(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, userData: UserData) => {
    try {
      console.log('Signing up user:', email);
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
          },
        },
      });

      if (error) {
        console.error('Sign up error:', error.message);
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive",
        });
        setIsLoading(false);
        throw error;
      } else {
        if (data.user) {
          await createDefaultWorkouts(data.user.id);
        }
        
        toast({
          title: "Sign up successful",
          description: "Please check your email to verify your account.",
        });
        
        console.log('Sign up successful - auth state change listener will update state');
      }
    } catch (error) {
      console.error("Error signing up:", error);
      setIsLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('Signing out user');
      setIsLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error.message);
        toast({
          title: "Sign out failed",
          description: error.message,
          variant: "destructive",
        });
        setIsLoading(false);
        throw error;
      }
      
      console.log('Sign out successful - state will be cleared by auth listener');
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const setUserAsAdmin = async (userId: string): Promise<boolean> => {
    if (!userId) {
      toast({
        title: "Error",
        description: "You must be logged in to perform this action",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: 'admin' })
        .eq('id', userId);
      
      if (error) {
        toast({
          title: "Error setting admin role",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Success",
        description: "Your account has been granted admin privileges",
      });
      return true;
    } catch (error) {
      console.error("Error setting admin role:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    signIn,
    signUp,
    signOut,
    setUserAsAdmin,
    isLoading,
  };
};
